import { memo } from 'react';

/**
 * Skeleton loading component for FeedCard
 */
const FeedCardSkeleton = memo(({ variant = 'default' }) => {
  // Card with image (for lost/found animals)
  if (variant === 'image') {
    return (
      <div className="bg-white rounded-xl border border-border overflow-hidden sm:flex sm:flex-row">
        {/* Image placeholder */}
        <div className="bg-gray-200 animate-pulse aspect-[4/3] sm:aspect-auto sm:w-36 md:w-44 lg:w-48 sm:flex-shrink-0" />
        
        {/* Content */}
        <div className="p-4 flex-1 flex flex-col justify-between min-h-0">
          {/* Top section */}
          <div className="space-y-2">
            {/* Title + Bookmark */}
            <div className="flex items-start justify-between gap-2">
              <div className="h-5 bg-gray-200 rounded animate-pulse flex-1" />
              <div className="h-7 w-7 bg-gray-200 rounded-full animate-pulse flex-shrink-0" />
            </div>

            {/* Description */}
            <div className="h-4 w-3/4 bg-gray-200 rounded animate-pulse" />

            {/* Location & Date */}
            <div className="flex items-center gap-2">
              <div className="h-3 w-20 bg-gray-200 rounded animate-pulse" />
              <div className="h-3 w-16 bg-gray-200 rounded animate-pulse" />
            </div>
          </div>

          {/* Contact button */}
          <div className="flex justify-center mt-3">
            <div className="h-9 w-24 bg-gray-200 rounded-lg animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  // Card without image (for lost/found animals without photos)
  if (variant === 'noimage') {
    return (
      <div className="bg-white rounded-xl border border-border overflow-hidden">
        {/* Content */}
        <div className="p-4 space-y-3">
          {/* Badge + Title + Bookmark */}
          <div className="flex items-start gap-2">
            <div className="h-6 w-14 bg-gray-200 rounded-full animate-pulse flex-shrink-0" />
            <div className="h-5 bg-gray-200 rounded animate-pulse flex-1" />
            <div className="h-7 w-7 bg-gray-200 rounded-full animate-pulse flex-shrink-0" />
          </div>

          {/* Description */}
          <div className="h-4 w-3/4 bg-gray-200 rounded animate-pulse" />

          {/* Location & Date */}
          <div className="flex items-center gap-2">
            <div className="h-3 w-20 bg-gray-200 rounded animate-pulse" />
            <div className="h-3 w-16 bg-gray-200 rounded animate-pulse" />
          </div>

          {/* Contact button */}
          <div className="flex justify-center pt-1">
            <div className="h-9 w-24 bg-gray-200 rounded-lg animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  // Default card (for PSAs)
  return (
    <div className="bg-white rounded-xl border border-border overflow-hidden">
      {/* Header */}
      <div className="bg-blue-50/50 px-4 py-2.5 border-b border-blue-100">
        <div className="flex items-center gap-2">
          <div className="h-7 w-7 bg-blue-100 rounded-full animate-pulse" />
          <div className="h-4 w-40 bg-blue-100 rounded animate-pulse" />
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-3">
        {/* Title + Bookmark */}
        <div className="flex items-start justify-between gap-3">
          <div className="h-5 w-2/3 bg-gray-200 rounded animate-pulse" />
          <div className="h-7 w-7 bg-gray-200 rounded-full animate-pulse flex-shrink-0" />
        </div>
        
        {/* Description */}
        <div className="space-y-2">
          <div className="h-4 w-full bg-gray-200 rounded animate-pulse" />
          <div className="h-4 w-5/6 bg-gray-200 rounded animate-pulse" />
        </div>

        {/* Date */}
        <div className="h-3 w-24 bg-gray-200 rounded animate-pulse pt-1" />
      </div>
    </div>
  );
});

/**
 * Stack of skeleton cards for loading state
 */
export const FeedSkeletonGrid = memo(({ count = 3 }) => {
  // Cycle through variants for visual variety
  const variants = ['image', 'default', 'noimage'];
  
  return (
    <div className="space-y-4 max-w-2xl mx-auto">
      {Array.from({ length: count }).map((_, index) => (
        <FeedCardSkeleton 
          key={index} 
          variant={variants[index % variants.length]} 
        />
      ))}
    </div>
  );
});

export default FeedCardSkeleton;
